<script setup lang="ts">
import { FocusImage } from "@/types";
import { defineProps, defineEmits, watch, nextTick, ref } from "vue";

const props = defineProps<Props>();
type Props = {
  selectedImage: FocusImage | null;
  steps: any;
};

const emits = defineEmits<Emits>();
type Emits = {
  (event: "setSelectedImage", image: FocusImage): void;
};

const selectedImage = ref<FocusImage | null>(null);

const handleSelectedImage = (
  image: any,
  stepId: any,
  inputId: any,
  repeatableInputIndex?: number
) => {
  let newFocusedImage: FocusImage = {
    logId: image.logId,
    imageUrl: image.imageUrl,
    comment: image.comment,
    inputId: inputId,
    stepId: stepId,
    repeatableGroupIndex: image.repeatableGroupIndex,
    repeatableInputIndex: repeatableInputIndex,
    terminId: image.terminId,
    workers: image.workers,
    createdAt: image.createdAt,
  };
  emits("setSelectedImage", newFocusedImage);
};

watch(
  () => props.selectedImage,
  async (newImage) => {
    if (newImage?.logId) {
      await nextTick();
      const element = document.getElementById(`image-${newImage.logId}`);
      element?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  },
  { immediate: true }
);

watch(
  () => props.selectedImage,
  (newImage) => {
    selectedImage.value = newImage ? { ...newImage } : null;
  },
  { immediate: true, deep: true }
);
</script>

<template>
  <div class="flex h-full overflow-y-auto flex-col gap-3">
    <!-- STEPS -->
    <div
      v-for="step in steps.filter((step:any)=> step.inputs.filter((input:any) =>(input.subtype === 'image' && input.value && input.value?.length > 0) || 
       (input.type === 'image' && input.value && input.value.length > 0)).length > 0)"
      :key="step.id"
      :id="step.id"
      class="rounded-primary-radius flex flex-col items-center justify-center cursor-pointer hover:opacity-100 duration-300 gap-3 mb-[24px]"
    >
      <!-- INPUTS -->
      <div
        v-for="input in step.inputs.filter((input:any)=> (input.subtype === 'image' || input.type === 'image') && input.value && input.value.length > 0)"
        :key="input.id"
        :id="input.id"
        class="w-full flex flex-col"
      >
        <div
          class="flex flex-col gap-2"
          v-if="input.value && input.value.length > 0"
        >
          <!-- IMAGES -->
          <div
            v-for="image in input.value"
            :key="image.logId"
            class="relative flex flex-col w-[50px] h-[50px]"
            :class="
              props.selectedImage?.logId === image.logId
                ? 'opacity-100'
                : 'opacity-30'
            "
          >
            <img
              @click="handleSelectedImage(image, step.id, input.id)"
              :id="`image-${image.logId}`"
              class="w-full h-full object-cover rounded-primary-radius"
              loading="lazy"
              :class="
                props.selectedImage?.logId === image.logId
                  ? 'border-[3px] border-[#393840]'
                  : 'border-transparent'
              "
              :src="image.imageUrl"
              alt="Image"
            />
            <div
              class="absolute bottom-0 right-0"
              v-if="
                selectedImage?.logId === image?.logId && selectedImage?.comment
              "
            >
              <img
                class="w-[17px] h-[17px]"
                src="@/assets/installation-image-comment.svg"
                alt="Commment"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
